import { useCallback } from 'react';
import type React from 'react';
import { arrayMove } from '@dnd-kit/sortable';

import toast from 'components/partials/toast/toast';

import {
  calcMidpointOrder,
  showReorderError,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-helpers';
import type {
  GroupWithRegimes,
  RegimeGroup,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

interface UpdateGroupMutation {
  mutateAsync(data: unknown): Promise<unknown>;
}

interface UseRegimesSidebarGroupActionsParams {
  userId: string;
  modelId: string | number;
  localGroups: RegimeGroup[];
  setLocalGroups: React.Dispatch<React.SetStateAction<RegimeGroup[]>>;
  groupsWithRegimes: GroupWithRegimes[];
  hasPendingGroupUpdate: boolean;
  pendingGroupUpdateRef: React.MutableRefObject<boolean>;
  pendingGroupIds: Set<string | number>;
  addPendingGroup: (groupId: string | number) => void;
  removePendingGroup: (groupId: string | number) => void;
  updateGroupMut: UpdateGroupMutation;
}

interface UseRegimesSidebarGroupActionsResult {
  handleGroupReorder: (activeGroupId: string | number, overGroupId: string | number) => void;
  handleGroupToggle: (group: RegimeGroup, checked: boolean) => void;
}

export const useRegimesSidebarGroupActions = ({
  userId,
  modelId,
  localGroups,
  setLocalGroups,
  groupsWithRegimes,
  hasPendingGroupUpdate,
  pendingGroupUpdateRef,
  pendingGroupIds,
  addPendingGroup,
  removePendingGroup,
  updateGroupMut,
}: UseRegimesSidebarGroupActionsParams): UseRegimesSidebarGroupActionsResult => {
  const handleGroupReorder = useCallback(
    (activeGroupId: string | number, overGroupId: string | number) => {
      if (activeGroupId === overGroupId) return;
      if (pendingGroupUpdateRef.current) return;
      if (pendingGroupIds.has(activeGroupId)) return;

      const oldIndex = groupsWithRegimes.findIndex((g) => g.id === activeGroupId);
      const newIndex = groupsWithRegimes.findIndex((g) => g.id === overGroupId);

      if (oldIndex < 0 || newIndex < 0) return;

      const reordered = arrayMove(groupsWithRegimes, oldIndex, newIndex);
      const newSortOrder = calcMidpointOrder(reordered, newIndex);
      const group = groupsWithRegimes.find((g) => g.id === activeGroupId);

      if (!group) return;

      const previousGroups = localGroups;

      pendingGroupUpdateRef.current = true;
      addPendingGroup(activeGroupId);
      setLocalGroups((prev) =>
        prev.map((g) =>
          g.id === activeGroupId ? { ...g, sortOrder: newSortOrder } : g,
        ),
      );

      updateGroupMut
        .mutateAsync({
          userId,
          modelId,
          groupId: activeGroupId,
          data: {
            name: group.name,
            enabled: group.enabled,
            sortOrder: newSortOrder,
          },
        })
        .catch(() => {
          setLocalGroups(previousGroups);
          showReorderError();
        })
        .finally(() => {
          pendingGroupUpdateRef.current = false;
          removePendingGroup(activeGroupId);
        });
    },
    [
      addPendingGroup,
      groupsWithRegimes,
      localGroups,
      pendingGroupIds,
      pendingGroupUpdateRef,
      removePendingGroup,
      setLocalGroups,
      updateGroupMut,
      userId,
      modelId,
    ],
  );

  const handleGroupToggle = useCallback(
    (group: RegimeGroup, checked: boolean) => {
      if (pendingGroupUpdateRef.current) return;
      if (hasPendingGroupUpdate) return;

      const previousGroups = localGroups;

      pendingGroupUpdateRef.current = true;
      addPendingGroup(group.id);
      setLocalGroups((prev) =>
        prev.map((g) =>
          g.id === group.id
            ? { ...g, enabled: checked }
            : { ...g, enabled: checked ? false : g.enabled },
        ),
      );

      updateGroupMut
        .mutateAsync({
          userId,
          modelId,
          groupId: group.id,
          data: { name: group.name, enabled: checked, sortOrder: group.sortOrder },
        })
        .catch(() => {
          setLocalGroups(previousGroups);
          toast.error({
            title: 'Error',
            message: 'Failed to update group. Please try again.',
          });
        })
        .finally(() => {
          pendingGroupUpdateRef.current = false;
          removePendingGroup(group.id);
        });
    },
    [
      addPendingGroup,
      hasPendingGroupUpdate,
      localGroups,
      pendingGroupUpdateRef,
      removePendingGroup,
      setLocalGroups,
      updateGroupMut,
      userId,
      modelId,
    ],
  );

  return {
    handleGroupReorder,
    handleGroupToggle,
  };
};
